/**
 * Minimal keyed tween runner (no three.js / DOM dependency in the runner —
 * node-testable). Driven by update(dt in ms) from the render loop.
 *
 * - Keyed tweens: add() with an existing key first *finishes* the old tween
 *   (snaps it to its end state, fires onComplete), so rapid inputs can never
 *   leave a mesh stranded mid-flight.
 * - onUpdate(eased, raw) receives both the eased progress and the raw
 *   normalized time (raw is used for shaped curves like the wall drop).
 * - loop: true wraps raw time modulo 1 forever (victory bounce); finishing a
 *   loop tween snaps it to t=1 (which loops are designed to treat as rest).
 */
export class TweenRunner {
  constructor() {
    this._tweens = new Map();
    this._anon = 0;
  }

  get size() {
    return this._tweens.size;
  }

  has(key) {
    return this._tweens.has(key);
  }

  /**
   * opts: { key?, duration(ms), ease?, loop?, onUpdate(eased, raw), onComplete? }
   * Returns the key. The first frame (t=0) is applied immediately.
   */
  add({ key, duration, ease = EASE_LINEAR, loop = false, onUpdate, onComplete }) {
    const k = key ?? `__anon:${this._anon++}`;
    this.finish(k);
    const tw = { duration: Math.max(1, duration), ease, loop, onUpdate, onComplete, elapsed: 0 };
    this._tweens.set(k, tw);
    onUpdate(ease(0), 0);
    return k;
  }

  /** Snap a tween to its end state (fires onComplete) and drop it. */
  finish(key) {
    const tw = this._tweens.get(key);
    if (!tw) return;
    this._tweens.delete(key);
    tw.onUpdate(tw.ease(1), 1);
    if (tw.onComplete) tw.onComplete();
  }

  finishPrefix(prefix) {
    for (const key of [...this._tweens.keys()]) {
      if (key.startsWith(prefix)) this.finish(key);
    }
  }

  finishAll() {
    for (const key of [...this._tweens.keys()]) this.finish(key);
  }

  /** Advance all tweens by dt milliseconds. */
  update(dt) {
    for (const [key, tw] of [...this._tweens]) {
      if (!this._tweens.has(key)) continue; // finished by an earlier callback
      tw.elapsed += dt;
      if (tw.loop) {
        const t = (tw.elapsed / tw.duration) % 1;
        tw.onUpdate(tw.ease(t), t);
      } else if (tw.elapsed >= tw.duration) {
        this.finish(key);
      } else {
        const t = tw.elapsed / tw.duration;
        tw.onUpdate(tw.ease(t), t);
      }
    }
  }
}

const EASE_LINEAR = (t) => t;

let mql = null;

/** Live check of the user's reduced-motion preference (false outside DOM). */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  if (!mql) mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mql.matches;
}
